import React, { useEffect, useState } from "react";
import { Copy, Check, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Compact "Get your embeddable Trust Index badge" panel for hospitals.
 * Backend `/api/badge/hospital/{id}/embed` returns the badge URL and the
 * ready-to-paste HTML + Markdown snippets. We render a live preview by
 * pointing an <img> at the SVG endpoint (it reads live data from Mongo).
 */
export default function TrustBadgeEmbed({ hospitalId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null); // "html" | "md" | "url" | null

  const load = async () => {
    if (!hospitalId) return;
    setLoading(true);
    try {
      const base = typeof window !== "undefined" ? window.location.origin : "";
      const { data: res } = await api.get(
        `/badge/hospital/${hospitalId}/embed`,
        { params: { request_base: base } }
      );
      setData(res);
    } catch (err) {
      console.error("Failed to load badge embed:", err);
      toast.error("Could not load badge details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hospitalId]);

  const copy = async (kind, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      toast.success(`${kind === "html" ? "HTML" : kind === "md" ? "Markdown" : "URL"} copied`);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Clipboard blocked — please copy manually");
    }
  };

  if (!data) {
    return (
      <div className="rounded-lg border border-border bg-white p-6" data-testid="trust-badge-embed-loading">
        <div className="text-sm text-muted-foreground">
          {loading ? "Loading badge…" : "No badge available yet."}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-white p-6" data-testid="trust-badge-embed">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Embeddable badge</div>
          <div className="mt-1 font-serif text-primary text-lg">Show your Trust Index anywhere</div>
          <p className="mt-1 text-xs text-muted-foreground max-w-md">
            Drop the badge on your own website. It reads a live score straight from Arogya Pramana —
            no cached numbers, no forgeries. Cached at the CDN for 30 minutes.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="badge-refresh">
          <RefreshCw size={12} className={"mr-1 " + (loading ? "animate-spin" : "")} /> Refresh
        </Button>
      </div>

      <div className="mt-5 flex flex-col md:flex-row items-start gap-6">
        {/* Live preview — bumping ?v=timestamp forces the CDN to serve fresh */}
        <div className="rounded-md border border-border bg-[#f8fafc] p-4">
          <img
            src={`${data.badge_url}?v=${Date.now()}`}
            alt={`Trust Index — ${data.name} — ${data.trust_score}/100`}
            width={260}
            height={80}
            data-testid="badge-preview"
          />
          <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-wide text-muted-foreground">
            <span>Live from /api/badge</span>
            <span>Score {data.trust_score} · {data.tier}</span>
          </div>
        </div>

        <div className="flex-1 min-w-0 w-full">
          <div className="space-y-3">
            <Snippet
              label="HTML (paste into your website)"
              value={data.html}
              onCopy={() => copy("html", data.html)}
              copied={copied === "html"}
              testId="badge-html"
            />
            <Snippet
              label="Markdown (for README / blog)"
              value={data.markdown}
              onCopy={() => copy("md", data.markdown)}
              copied={copied === "md"}
              testId="badge-md"
            />
            <Snippet
              label="Badge URL"
              value={data.badge_url}
              onCopy={() => copy("url", data.badge_url)}
              copied={copied === "url"}
              testId="badge-url"
              extra={
                <a
                  href={data.badge_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary underline inline-flex items-center gap-1"
                >
                  Open <ExternalLink size={10} />
                </a>
              }
            />
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">
            Anyone clicking the badge lands on your public Arogya Pramana profile. The score always reflects the current DB value —
            you don't need to re-embed when it changes.
          </p>
        </div>
      </div>
    </div>
  );
}

function Snippet({ label, value, onCopy, copied, testId, extra }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        {extra}
      </div>
      <div className="flex items-stretch gap-2">
        <code
          className="flex-1 rounded-md bg-muted/60 px-3 py-2 text-[11px] leading-snug text-primary break-all font-mono"
          data-testid={`${testId}-code`}
        >
          {value}
        </code>
        <button
          onClick={onCopy}
          className="shrink-0 rounded-md border border-border bg-white px-3 text-xs hover:bg-muted transition-colors"
          data-testid={`${testId}-copy`}
        >
          {copied ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} />}
        </button>
      </div>
    </div>
  );
}
